// Persistence helpers for run-log panels.
//
// Each run-log panel stores its selections in localStorage under its UUID.
// This logic used to live inline inside <RunLog> (setLocalStorage /
// LoadSavedSelections) and inside <App> (Event/Session/Reverse handlers),
// which made it impossible to unit test and hid a data-loss bug: an empty
// write on mount could wipe a saved panel, and an unguarded JSON.parse crashed
// on any missing/corrupt entry. See runLogStorage.test.js.

// Fields we persist for each run-log panel.
export const PERSISTED_FIELDS = [
    "DB",
    "Event",
    "TeamVehicle",
    "Session",
    "Live",
    "reverseOutingOrder",
];

// Build a storage object from a selections bag, keeping only the values that
// are set (mirrors the historical "if (selectedX) storageObj.X = selectedX").
export function buildSelections(selections = {}) {
    const obj = {};
    for (const field of PERSISTED_FIELDS) {
        if (selections[field]) {
            obj[field] = selections[field];
        }
    }
    return obj;
}

// Parse a stored value back into a selections object. Never throws: a missing
// (null) or corrupt entry yields {} rather than crashing the caller.
export function parseSelections(raw) {
    if (!raw) {
        return {};
    }
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

export function readSelections(id, storage = localStorage) {
    return parseSelections(storage.getItem(id));
}

// Persist selections for a panel. Returns true if it actually wrote.
//
// Fix for the run-history loss: never clobber a saved entry with an empty
// object. The component's effects fire with empty state on mount (before the
// async restore runs); if we wrote that empty state we'd wipe the user's saved
// panel. When there is nothing to persist we leave the prior value untouched.
export function writeSelections(id, selections, storage = localStorage) {
    const obj = buildSelections(selections);
    if (Object.keys(obj).length === 0) {
        return false;
    }
    storage.setItem(id, JSON.stringify(obj));
    return true;
}

// Update a single field on an existing entry (used by the top-level
// Event/Session/Reverse changes that fan out to every panel). Uses the guarded
// parse above so a missing/corrupt entry can't throw and abort the fan-out
// loop partway through the panel list.
export function updateSavedField(id, key, value, storage = localStorage) {
    const obj = parseSelections(storage.getItem(id));
    obj[key] = value;
    storage.setItem(id, JSON.stringify(obj));
    return obj;
}
